const heathrowReviews = {
  minibus: [
    ["Amelia R.", "The Heathrow minibus quote kept our family and luggage together after a long flight."],
    ["Vikram S.", "Clear terminal pickup details and enough room for bags, pushchairs and eight passengers."],
    ["Laura P.", "Useful live Heathrow checks before we confirmed the minibus for our hotel group."],
  ],
  coach: [
    ["Mark D.", "The Heathrow coach page made a large airport transfer easier to scope before we booked."],
    ["Fatima K.", "Good support for passenger numbers, luggage and coach timing around Terminal 5."],
    ["James L.", "Clear planning for a corporate group arriving at Heathrow and heading into London."],
  ],
};

const heathrowProofCards = [
  {
    eyebrow: "Licensed operators",
    title: "Registered and insured",
    body: "Heathrow group transfers are handled by licensed private hire, minibus or PSV partners according to vehicle type.",
  },
  {
    eyebrow: "Airport aware",
    title: "Live Heathrow planning",
    body: "Terminal, flight, queue, weather and road signals are shown before a group commits to the journey.",
  },
  {
    eyebrow: "Group travel",
    title: "Built around passengers and bags",
    body: "Quotes start with vehicles sized for families, schools, hotels, companies, tours and event passengers.",
  },
];

const heathrowChooseCards = [
  {
    title: "All Heathrow terminals",
    body: "Plan pickups and drop-offs for Terminals 2, 3, 4 and 5, with terminal pressure and queue context visible before booking.",
  },
  {
    title: "One vehicle for the group",
    body: "Avoid splitting passengers, suitcases and arrival instructions across separate taxis after a flight.",
  },
  {
    title: "Hotels, events and tours",
    body: "Useful for Heathrow hotels, London venues, Windsor stays, race days, school trips, conference groups and tour itineraries.",
  },
  {
    title: "Fixed quote flow",
    body: "Enter the airport journey once, compare available group vehicles and continue through the same booking path.",
  },
];

const heathrowRouteContentCards = [
  {
    title: "Terminal 2, 3, 4 and 5",
    body: "Use Heathrow Airport or choose a specific terminal. Terminal choice can affect approach roads, pickup points, waiting time and loading.",
  },
  {
    title: "Heathrow hotels and Bath Road",
    body: "Cover hotel groups around Bath Road, airport campus hotels, conference stays and early morning departures.",
  },
  {
    title: "Popular local Heathrow routes",
    body: "Group transfers regularly run between Heathrow and Slough, Maidenhead, Windsor, Ascot, Reading, London and wider South East locations.",
    links: [
      { to: "/slough-to-heathrow-minibus", label: "Slough route" },
      { to: "/windsor-to-heathrow-minibus", label: "Windsor route" },
      { to: "/ascot-to-heathrow-minibus", label: "Ascot route" },
    ],
  },
  {
    title: "Flight and queue timing",
    body: "Live status helps groups understand flight disruption, terminal pressure, queues, weather and road context before committing.",
  },
  {
    title: "Luggage and equipment",
    body: "Allow room for suitcases, pushchairs, sports bags, school kit, exhibition material or cruise luggage when choosing vehicle size.",
  },
  {
    title: "Minibus or coach choice",
    body: "Minibus works well for 8 to 16 passengers. Coach hire is better for 25+ passenger groups, schools, tours and corporate movements.",
  },
];

const heathrowFaqItems = [
  {
    question: "Can we book from any Heathrow terminal?",
    answer: "Yes. Enter Heathrow Airport or a specific terminal. Terminal 2, 3, 4 and 5 journeys can be planned through the quote flow.",
  },
  {
    question: "Do you track flights for Heathrow group transfers?",
    answer: "Yes. Heathrow flight and terminal context is part of the live status layer, and flight details can be added during booking where relevant.",
  },
  {
    question: "Can you handle hotel, school or corporate groups?",
    answer: "Yes. The quote flow supports family groups, hotels, school trips, sports teams, event guests, corporate arrivals and conference transfers.",
  },
  {
    question: "Should we choose a minibus or coach?",
    answer: "Use a minibus for 8 to 16 passengers. For 25+ passengers or larger luggage movements, use the Heathrow coach page so the quote starts with larger vehicle options.",
  },
];

const airportMinibusDetails = {
  gatwick: {
    name: "Gatwick",
    code: "LGW",
    terminals: "North and South terminals",
    roads: "M23, M25 and A23",
    rail: "Gatwick Airport railway station",
    areas: ["London", "Surrey", "Brighton", "Crawley", "Kent"],
  },
  luton: {
    name: "Luton",
    code: "LTN",
    terminals: "Luton Airport terminal",
    roads: "M1, A1081 and local airport roads",
    rail: "Luton DART and Luton Airport Parkway",
    areas: ["London", "Bedfordshire", "Hertfordshire", "Milton Keynes", "the Midlands"],
  },
  stansted: {
    name: "Stansted",
    code: "STN",
    terminals: "Stansted Airport terminal",
    roads: "M11, A120 and local airport roads",
    rail: "Stansted Express and Stansted Airport station",
    areas: ["London", "Essex", "Cambridge", "Hertfordshire", "East Anglia"],
  },
  manchester: {
    name: "Manchester",
    serviceName: "Manchester Airport",
    fullName: "Manchester Airport",
    path: "/manchester-airport-minibus-hire",
    code: "MAN",
    terminals: "Terminals 1, 2 and 3",
    roads: "M56, M60 and local airport roads",
    rail: "Manchester Airport railway station",
    areas: ["Manchester", "Greater Manchester", "Cheshire", "Liverpool", "the North West"],
  },
};

const buildAirportMinibusService = (key) => {
  const airport = airportMinibusDetails[key];
  const serviceName = airport.serviceName || airport.name;
  const airportName = airport.fullName || `${airport.name} Airport`;
  const nearbyAreas = airport.areas.join(", ");

  return {
    configs: {
      minibus: {
        brand: "MinibusGrab",
        domain: "https://www.minibusgrab.com",
        className: "minibusgrab-v2-home",
        vehicle: "minibus",
        path: airport.path || `/${key}-minibus-hire`,
        title: `${serviceName} Minibus Hire | 8 & 16 Seater Transfers`,
        description: `Get a fixed quote for ${serviceName} minibus hire. 8 and 16 seater group transfers with passenger, luggage and vehicle availability checked by our team.`,
        keywords: `${serviceName} minibus hire, ${serviceName} 8 seater, ${serviceName} 16 seater, minibus to ${serviceName}, ${serviceName} group transfer`,
        h1: `${serviceName} Minibus Hire – 8 & 16 Seater Transfers`,
        intro: `Group transfers to and from ${airportName}, with passenger numbers, luggage, pickup details and vehicle availability checked before we confirm your fixed price.`,
        quoteLabel: "Get My Fixed Quote",
        passengers: 8,
        bags: 0,
        resultPath: "/minibus-results",
        resultQuery: "vehicles=nine,fourteen",
        seats: "8 & 16 seaters",
        fleet: ["8 passenger / 9-seater minibuses", "14 to 16 passenger minibuses", "Luggage-ready licensed operator options"],
      },
    },
    route: {
      routeKey: key,
      liveStripVariant: key,
      pageClassName: `${key}-service-v2`,
      heroEyebrow: `${serviceName} group transfers`,
      metrics: [airport.code, airport.terminals, "__SEATS__", "Live journey checks"],
      areas: [airportName, ...airport.areas],
      seo: {
        routeName: `${serviceName} group transfer`,
        serviceType: `${serviceName} minibus hire`,
        areaServed: [airportName, ...airport.areas],
      },
      quote: {
        formId: `${key}-minibus-quote`,
        pickupDefault: airportName,
        dropoffDefault: "",
        pickupAriaLabel: `${serviceName} pickup terminal, postcode or address`,
        dropoffAriaLabel: "Destination postcode or address",
        pickupPlaceholder: `Enter ${serviceName} pickup point`,
        dropoffPlaceholder: "Enter destination postcode or address",
        genericError: `We could not calculate this ${serviceName} minibus quote. Please check the journey details and try again.`,
      },
      showHeathrowStatus: key === "manchester",
      statusAirportCode: airport.code,
      statusAirportName: airportName,
      compactStatusTitle: `Live ${serviceName} group transfer checks`,
      compactStatusKicker: `Live ${airport.code} intelligence`,
      compactStatusFootnote: key === "manchester"
        ? "Roads, flights, terminals, rail and weather are checked before Manchester Airport group journeys."
        : "Roads, flights, terminals, rail and weather are checked before group airport journeys.",
      fullStatusPath: null,
      showRouteIntelligence: false,
      showPricePreview: false,
      reviews: {
        minibus: [
          ["Sarah M.", `The ${serviceName} group quote made it easy to explain our passenger and luggage needs.`],
          ["Daniel K.", `Useful fixed-quote process for keeping our group together for the ${serviceName} transfer.`],
          ["Amina R.", `Clear support for vehicle size, luggage and our ${serviceName} pickup details.`],
        ],
      },
      proofCards: [
        {
          eyebrow: "Licensed operators",
          title: "Registered and insured",
          body: `${serviceName} group transfers are handled by licensed private hire, minibus or PSV operators according to vehicle type.`,
        },
        {
          eyebrow: "Airport-aware planning",
          title: `${airport.terminals}`,
          body: `Pickup point, flight timing, group loading and airport access are checked before the journey is confirmed.`,
        },
        {
          eyebrow: "Accurate fixed quote",
          title: "Passengers, bags and availability",
          body: "Our team checks the details needed to match the group with a suitable vehicle and confirmed fixed price.",
        },
      ],
      chooseTitle: `Built for groups using ${serviceName}`,
      chooseCards: [
        { title: "One vehicle for the group", body: "Keep passengers, luggage and pickup instructions together instead of coordinating several taxis." },
        { title: "Airport pickup details", body: `Provide the flight, ${airport.terminals.toLowerCase()} and meeting instructions so the pickup can be planned correctly.` },
        { title: "8 or 16 seats", body: "Tell us the passenger and luggage totals so we can confirm the suitable minibus capacity." },
        { title: "Fixed price confirmed", body: "Vehicle availability and journey details are checked before your accurate fixed quote is sent." },
      ],
      routeContentCards: [
        { title: `${serviceName} pickup and drop-off`, body: `The page supports group journeys both to and from ${airportName}, including airport hotels and agreed meeting points.` },
        { title: "Road journey context", body: `${airport.roads} can affect transfer time. Use the exact pickup and destination so the route can be checked accurately.` },
        { title: "Rail and disruption context", body: `${airport.rail} can affect passenger arrival plans and meeting times for the group.` },
        { title: "Luggage and family needs", body: "Include suitcases, pushchairs, child-seat requests, mobility needs, sports kit or other large items in the enquiry." },
        { title: "Regional and UK transfers", body: `${serviceName} minibuses can serve group journeys involving ${nearbyAreas}.` },
        { title: "Multiple stops and larger groups", body: "Tell us about extra stops, separate pickups or more than 16 passengers so the team can recommend the right option." },
      ],
      faqItems: [
        { question: `Can we book a minibus to or from ${serviceName}?`, answer: `Yes. Enter ${airportName} as either the pickup or destination and provide the exact airport meeting details during the enquiry.` },
        { question: "Can we get an instant fixed price?", answer: "For 8 and 16 seater journeys, we quickly confirm passenger numbers, luggage, route details and current vehicle availability before sending the accurate fixed price." },
        { question: "How much luggage can we bring?", answer: "Capacity depends on the vehicle and passenger total. Include all suitcases and large items so the correct option can be confirmed." },
        { question: "Can you help families and accessibility needs?", answer: "Yes. Add child-seat, elderly traveller, mobility or accessibility requirements so they can be considered before confirmation." },
      ],
      routeOverviewTitle: `${serviceName} minibus transfer planning`,
      routeOverviewIntro: `A reliable group quote needs the exact airport point, passenger and luggage totals, destination, travel time and current vehicle availability.`,
      vehicleContextTitle: () => `${serviceName} 8 and 16 seater options`,
      faqEyebrow: `${serviceName} minibus FAQ`,
      faqTitle: "What groups need to know before requesting a quote",
      support: {
        title: `Need help planning a ${serviceName} group transfer?`,
        body: "Use the quote form first, or contact support for multiple stops, child seats, accessibility needs, unusually large luggage or time-sensitive airport pickups.",
        nudgeText: `Need ${serviceName} minibus help?`,
      },
    },
  };
};

const legolandMinibusService = {
  configs: {
    minibus: {
      brand: "MinibusGrab",
      domain: "https://www.minibusgrab.com",
      className: "minibusgrab-v2-home",
      vehicle: "minibus",
      path: "/legoland-minibus-hire",
      title: "LEGOLAND Minibus Hire | 8 & 16 Seater Group Transfers",
      description: "Get a fixed quote for 8 and 16 seater minibus hire to LEGOLAND Windsor. Passenger, luggage, child-seat and vehicle availability checked by our team.",
      keywords: "LEGOLAND minibus hire, minibus to LEGOLAND Windsor, Heathrow to LEGOLAND minibus, London to LEGOLAND minibus, 8 seater LEGOLAND, 16 seater LEGOLAND",
      h1: "LEGOLAND Minibus Hire – 8 & 16 Seater Group Transfers",
      intro: "Keep the family or group together to LEGOLAND Windsor, with luggage, pushchairs, child-seat needs, pickup timing and vehicle availability checked before we confirm your fixed price.",
      quoteLabel: "Get My LEGOLAND Fixed Quote",
      passengers: 8,
      bags: 0,
      resultPath: "/minibus-results",
      resultQuery: "vehicles=nine,fourteen",
      seats: "8 & 16 seaters",
      fleet: ["8 passenger / 9-seater minibuses", "14 to 16 passenger minibuses", "Luggage and pushchair-ready licensed operator options"],
    },
  },
  route: {
    routeKey: "legoland",
    liveStripVariant: "legoland",
    liveSummary: "legoland",
    pageClassName: "legoland-minibus-service-v2",
    heroEyebrow: "LEGOLAND Windsor group travel",
    heroBenefits: [
      { icon: "✓", title: "Accurate fixed quote", body: "Checked by our team" },
      { icon: "♙", title: "Children & families", body: "Needs considered" },
      { icon: "▣", title: "Luggage & pushchairs", body: "Space confirmed" },
      { icon: "☎", title: "Human support", body: "Help when needed" },
    ],
    metrics: ["SL4 4AY", "Families + groups", "__SEATS__", "Live park checks"],
    areas: ["LEGOLAND Windsor Resort", "Heathrow Airport", "London", "Slough", "Windsor", "Gatwick Airport"],
    seo: {
      routeName: "LEGOLAND Windsor minibus transfer",
      serviceType: "LEGOLAND minibus hire",
      areaServed: ["LEGOLAND Windsor Resort", "Heathrow Airport", "London", "Slough", "Windsor", "South East England"],
      destination: "LEGOLAND Windsor Resort, SL4 4AY",
    },
    quote: {
      formId: "legoland-minibus-quote",
      pickupDefault: "",
      dropoffDefault: "LEGOLAND Windsor Resort, SL4 4AY",
      pickupAriaLabel: "Pickup postcode or address",
      dropoffAriaLabel: "LEGOLAND destination",
      pickupPlaceholder: "Enter pickup postcode or address",
      dropoffPlaceholder: "LEGOLAND Windsor Resort, SL4 4AY",
      genericError: "We could not calculate this LEGOLAND minibus quote. Please check the journey details and try again.",
    },
    showHeathrowStatus: false,
    showRouteIntelligence: false,
    showPricePreview: false,
    quotePromises: [
      "No payment taken now",
      "Passenger and luggage needs checked",
      "Child-seat requests considered",
      "Vehicle availability confirmed",
      "Fixed price sent by our team",
    ],
    reviews: { minibus: [] },
    proofCards: [
      {
        eyebrow: "Family group planning",
        title: "Passengers, children and bags",
        body: "Tell us about children, pushchairs, suitcases, mobility needs and any child-seat requests before the vehicle is confirmed.",
      },
      {
        eyebrow: "Live destination context",
        title: "Park, road, rail and weather checks",
        body: "The page uses selected live signals from our full LEGOLAND budget and journey planner without distracting from your quote.",
      },
      {
        eyebrow: "Licensed operators",
        title: "A suitable group vehicle",
        body: "Your journey is matched with a licensed private hire, minibus or PSV operator according to the required vehicle type.",
      },
    ],
    chooseTitle: "Why families and groups use one minibus",
    chooseCards: [
      { title: "Travel together", body: "Avoid splitting children, adults, bags and arrival instructions across several cars." },
      { title: "Space checked first", body: "Passenger and luggage totals help the team confirm whether an 8 or 16 seater is suitable." },
      { title: "Park-day timing", body: "Opening hours, road pressure, pickup loading and the return collection time can all affect the plan." },
      { title: "No payment now", body: "Request the accurate fixed quote first. Availability and journey details are checked before confirmation." },
    ],
    routeContentCards: [
      { title: "Heathrow to LEGOLAND", body: "Useful for families arriving with suitcases, pushchairs or child-seat requirements who want one direct group transfer." },
      { title: "London to LEGOLAND", body: "Plan a group pickup from a London hotel, station or postcode and provide the exact return collection time if required." },
      { title: "Slough and Windsor", body: "Shorter local transfers can start at hotels, stations, Windsor town centre or an agreed group meeting point." },
      { title: "Other London airports", body: "Gatwick, Luton and Stansted group journeys can be quoted when flight, luggage and pickup details are supplied." },
      { title: "School and organised groups", body: "Include lead-passenger details, group size, accessibility requirements, equipment and any multiple pickup points." },
      { title: "Return after the park closes", body: "Confirm the collection point and realistic pickup time so the driver and group can meet without confusion." },
    ],
    faqItems: [
      { question: "Can you take us directly to LEGOLAND Windsor?", answer: "Yes. The quote form is preset to LEGOLAND Windsor Resort, SL4 4AY. Enter the exact home, hotel, station or airport pickup." },
      { question: "Can we request child seats?", answer: "Yes. Tell us the number and ages of children so available child-seat options can be checked before confirmation." },
      { question: "Will an 8 seater hold passengers and pushchairs?", answer: "It depends on the passenger total and luggage volume. List every suitcase, pushchair and large item so the correct capacity can be confirmed." },
      { question: "Can we book a return journey?", answer: "Yes. Include the planned return date, collection time and meeting point. Park closing and exit traffic should be considered." },
    ],
    routeOverviewTitle: "Popular group journeys to LEGOLAND Windsor",
    routeOverviewIntro: "The best minibus plan depends on the starting point, passenger and luggage totals, children or accessibility needs, visit date, arrival target and return collection time.",
    vehicleContextTitle: () => "LEGOLAND 8 and 16 seater options",
    faqEyebrow: "LEGOLAND minibus FAQ",
    faqTitle: "What families and groups need to know",
    support: {
      title: "Need help planning the LEGOLAND group journey?",
      body: "Use the quote form first, or contact support for child seats, accessibility needs, multiple families, airport luggage, several pickup points or a return after park closing.",
      nudgeText: "Need LEGOLAND minibus help?",
    },
  },
};

const attractionRouteDetails = {
  "heathrow-legoland": {
    path: "/heathrow-to-legoland-minibus",
    title: "Heathrow to LEGOLAND Minibus | 8 & 16 Seater Transfers",
    description: "Get a fixed quote for Heathrow to LEGOLAND Windsor minibus transfers. 8 and 16 seater options with flight, luggage, child-seat and availability checks.",
    keywords: "Heathrow to LEGOLAND minibus, Heathrow LEGOLAND 8 seater, Heathrow LEGOLAND 16 seater, group transfer Heathrow to LEGOLAND",
    h1: "Heathrow to LEGOLAND Minibus – 8 & 16 Seater Transfers",
    intro: "Keep the family, airport luggage and pushchairs together from Heathrow to LEGOLAND Windsor while our team checks passenger needs, flight details and vehicle availability before confirming the fixed price.",
    eyebrow: "Heathrow to LEGOLAND group transfer",
    pickup: "Heathrow Airport",
    dropoff: "LEGOLAND Windsor Resort, SL4 4AY",
    pickupLabel: "Heathrow terminal or pickup point",
    destinationLabel: "LEGOLAND destination",
    strip: "legoland",
    liveSummary: "legoland",
    destinationName: "LEGOLAND Windsor Resort",
    postcode: "SL4 4AY",
    plannerPath: "/legoland-budget-planner",
    routeName: "Heathrow to LEGOLAND minibus transfer",
    metrics: ["Heathrow T2–T5", "SL4 4AY", "__SEATS__", "Live park checks"],
    journeyCards: [
      { title: "Every Heathrow terminal", body: "Specify Terminal 2, 3, 4 or 5, the flight number and the preferred meeting arrangement." },
      { title: "Airport luggage and pushchairs", body: "List every suitcase, pushchair, child-seat request and large item so the vehicle capacity can be checked accurately." },
      { title: "Flight-aware pickup", body: "Arrival timing, baggage reclaim and group meeting instructions should be clear before dispatch is confirmed." },
      { title: "Direct to the park", body: "One minibus keeps the family together for the transfer to LEGOLAND Windsor Resort, SL4 4AY." },
    ],
  },
  "london-legoland": {
    path: "/london-to-legoland-minibus",
    title: "London to LEGOLAND Minibus | 8 & 16 Seater Transfers",
    description: "Get a fixed quote for London to LEGOLAND Windsor minibus hire. 8 and 16 seater group transfers for families, hotels and organised groups.",
    keywords: "London to LEGOLAND minibus, Central London LEGOLAND transfer, 8 seater London to LEGOLAND, 16 seater LEGOLAND minibus",
    h1: "London to LEGOLAND Minibus – 8 & 16 Seater Transfers",
    intro: "Travel together from a London hotel, station or postcode to LEGOLAND Windsor, with passenger numbers, pushchairs, luggage, pickup access and vehicle availability checked before the fixed price is confirmed.",
    eyebrow: "London to LEGOLAND group travel",
    pickup: "Central London",
    dropoff: "LEGOLAND Windsor Resort, SL4 4AY",
    pickupLabel: "London pickup postcode, hotel or address",
    destinationLabel: "LEGOLAND destination",
    strip: "legoland",
    liveSummary: "legoland",
    destinationName: "LEGOLAND Windsor Resort",
    postcode: "SL4 4AY",
    plannerPath: "/legoland-budget-planner",
    routeName: "London to LEGOLAND minibus transfer",
    metrics: ["London pickup", "SL4 4AY", "__SEATS__", "Live park checks"],
    journeyCards: [
      { title: "Hotels, stations and homes", body: "Use the exact London postcode, hotel entrance, station meeting point or residential pickup address." },
      { title: "Keep families together", body: "One group vehicle avoids coordinating multiple cars for adults, children, bags and pushchairs." },
      { title: "London traffic matters", body: "The pickup area, time of day and route towards Windsor can materially affect the journey plan." },
      { title: "Plan the return", body: "Include the park collection point and realistic return time if the group is travelling back to London." },
    ],
  },
  "heathrow-windsor-castle": {
    path: "/heathrow-to-windsor-castle-minibus",
    title: "Heathrow to Windsor Castle Minibus | 8 & 16 Seater Transfers",
    description: "Get a fixed quote for Heathrow to Windsor Castle minibus transfers. 8 and 16 seater options for families, tours and airport groups.",
    keywords: "Heathrow to Windsor Castle minibus, Heathrow Windsor group transfer, 8 seater Windsor Castle, 16 seater Heathrow Windsor",
    h1: "Heathrow to Windsor Castle Minibus – 8 & 16 Seater Transfers",
    intro: "Travel together from Heathrow to Windsor Castle with airport luggage, visitor timing, passenger needs and suitable drop-off arrangements checked before the fixed price is confirmed.",
    eyebrow: "Heathrow to Windsor Castle groups",
    pickup: "Heathrow Airport",
    dropoff: "Windsor Castle, SL4 1NJ",
    pickupLabel: "Heathrow terminal or pickup point",
    destinationLabel: "Windsor Castle destination",
    strip: "windsor",
    destinationName: "Windsor Castle",
    postcode: "SL4 1NJ",
    plannerPath: "/windsor-castle-budget-planner",
    routeName: "Heathrow to Windsor Castle minibus transfer",
    metrics: ["Heathrow T2–T5", "SL4 1NJ", "__SEATS__", "Visitor planning"],
    journeyCards: [
      { title: "Heathrow terminal pickup", body: "Provide the terminal, flight details, group meeting instructions and total airport luggage." },
      { title: "Castle opening days", body: "Windsor Castle can close on selected days. Check the visit date in the full planner before committing to travel." },
      { title: "Central Windsor access", body: "The exact legal drop-off and collection point should be agreed for a group arriving near the Castle." },
      { title: "Hotels and onward travel", body: "Add a Windsor hotel, return to Heathrow or other stop when requesting the personalised journey plan." },
    ],
  },
  "thorpe-park": {
    path: "/thorpe-park-minibus-hire",
    title: "Thorpe Park Minibus Hire | 8 & 16 Seater Group Transfers",
    description: "Get a fixed quote for 8 and 16 seater minibus hire to Thorpe Park. Group travel with passenger, luggage and vehicle availability checked.",
    keywords: "Thorpe Park minibus hire, minibus to Thorpe Park, London to Thorpe Park minibus, 8 seater Thorpe Park, 16 seater Thorpe Park",
    h1: "Thorpe Park Minibus Hire – 8 & 16 Seater Group Transfers",
    intro: "Keep friends, families or organised groups together to Thorpe Park, with the exact pickup, passenger needs, return timing and vehicle availability checked before the fixed price is confirmed.",
    eyebrow: "Thorpe Park group travel",
    pickup: "",
    dropoff: "Thorpe Park Resort, KT16 8PN",
    pickupLabel: "Pickup postcode or address",
    destinationLabel: "Thorpe Park destination",
    strip: "thorpe",
    destinationName: "Thorpe Park Resort",
    postcode: "KT16 8PN",
    plannerPath: "/thorpepark-budget-planner",
    routeName: "Thorpe Park minibus transfer",
    metrics: ["KT16 8PN", "Friends + groups", "__SEATS__", "Park-day planning"],
    journeyCards: [
      { title: "London and regional groups", body: "Start from a home, hotel, station or agreed meeting point and provide the full pickup postcode." },
      { title: "One vehicle for the group", body: "Keep everyone together instead of coordinating multiple cars before and after a long park day." },
      { title: "Staines and Chertsey context", body: "Rail visitors still need a final connection; a direct minibus can reduce changes and group coordination." },
      { title: "Return after closing", body: "Agree the collection point and time carefully because park-exit and local road pressure can build after closing." },
    ],
  },
};

const buildAttractionRouteService = (key) => {
  const item = attractionRouteDetails[key];
  return {
    configs: {
      minibus: {
        brand: "MinibusGrab",
        domain: "https://www.minibusgrab.com",
        className: "minibusgrab-v2-home",
        vehicle: "minibus",
        path: item.path,
        title: item.title,
        description: item.description,
        keywords: item.keywords,
        h1: item.h1,
        intro: item.intro,
        quoteLabel: "Get My Fixed Quote",
        passengers: 8,
        bags: 0,
        resultPath: "/minibus-results",
        resultQuery: "vehicles=nine,fourteen",
        seats: "8 & 16 seaters",
        fleet: ["8 passenger / 9-seater minibuses", "14 to 16 passenger minibuses", "Licensed group-travel operator options"],
      },
    },
    route: {
      routeKey: key,
      liveStripVariant: item.strip,
      liveSummary: item.liveSummary,
      pageClassName: `${key}-minibus-service-v2`,
      heroEyebrow: item.eyebrow,
      heroBenefits: [
        { icon: "✓", title: "Accurate fixed quote", body: "Checked by our team" },
        { icon: "♙", title: "Group needs", body: "Passengers considered" },
        { icon: "▣", title: "Bags & equipment", body: "Space confirmed" },
        { icon: "☎", title: "Human support", body: "Help when needed" },
      ],
      metrics: item.metrics,
      areas: [item.destinationName, "London", "Heathrow Airport", "South East England"],
      seo: {
        routeName: item.routeName,
        serviceType: item.routeName,
        areaServed: [item.destinationName, "London", "Heathrow Airport", "South East England"],
        destination: `${item.destinationName}, ${item.postcode}`,
      },
      quote: {
        formId: `${key}-minibus-quote`,
        pickupDefault: item.pickup,
        dropoffDefault: item.dropoff,
        pickupAriaLabel: item.pickupLabel,
        dropoffAriaLabel: item.destinationLabel,
        pickupPlaceholder: item.pickupLabel,
        dropoffPlaceholder: item.dropoff,
        genericError: `We could not calculate this ${item.destinationName} minibus quote. Please check the journey details and try again.`,
      },
      showHeathrowStatus: false,
      showRouteIntelligence: false,
      showPricePreview: false,
      quotePromises: ["No payment taken now", "Passenger needs checked", "Luggage space confirmed", "Vehicle availability checked", "Fixed price sent by our team"],
      reviews: { minibus: [] },
      proofCards: [
        { eyebrow: "Accurate fixed quote", title: "Passenger and vehicle details checked", body: "Group size, luggage, pickup access, travel timing and suitable vehicle availability are checked before confirmation." },
        { eyebrow: "Destination-aware planning", title: `${item.destinationName} visitor journey`, body: `The page uses verified context from our existing ${item.destinationName} planning tools without adding the full planner to the booking flow.` },
        { eyebrow: "Licensed operators", title: "Registered and insured", body: "Journeys are matched with licensed private hire, minibus or PSV operators according to the required vehicle type." },
      ],
      chooseTitle: `Plan the group journey to ${item.destinationName}`,
      chooseCards: item.journeyCards,
      routeContentCards: [
        { title: "Passengers and seating", body: "Confirm the number of adults and children and mention elderly travellers, mobility needs or child-seat requests." },
        { title: "Luggage and equipment", body: "List suitcases, pushchairs, wheelchairs, sports kit and other large items before the vehicle is selected." },
        { title: "Exact pickup access", body: "Use a full postcode or address and provide hotel, station, airport terminal or meeting-point instructions." },
        { title: "Arrival and return timing", body: "Include the target arrival and return collection time so park opening, closing and road pressure can be considered." },
        { title: "Multiple pickups", body: "Tell the team about different families, hotels or meeting points because extra stops change timing and availability." },
        { title: "Full destination planner", body: `Use the dedicated planner for wider ticket, rail, road, parking or weather context.`, links: [{ to: item.plannerPath, label: `Open ${item.destinationName} planner` }] },
      ],
      faqItems: [
        { question: `Can you take our group directly to ${item.destinationName}?`, answer: `Yes. The destination is preset to ${item.destinationName}, ${item.postcode}. Enter or confirm the exact pickup details.` },
        { question: "Why is the minibus price checked first?", answer: "Passenger numbers, luggage, route timing, pickup access and current vehicle availability can materially change the suitable option and fixed price." },
        { question: "Can we arrange a return journey?", answer: "Yes. Provide the return date, collection time and meeting point so the full journey can be confirmed." },
        { question: "Can you support children or accessibility needs?", answer: "Yes. Add child-seat, elderly traveller, mobility and accessibility requirements to the enquiry before confirmation." },
      ],
      routeOverviewTitle: `${item.destinationName} group journey details`,
      routeOverviewIntro: "A dependable minibus quote starts with the exact pickup, passenger and luggage totals, destination, travel date, arrival target and return requirements.",
      vehicleContextTitle: () => `${item.destinationName} 8 and 16 seater options`,
      faqEyebrow: `${item.destinationName} minibus FAQ`,
      faqTitle: "What groups need to know before requesting a quote",
      support: {
        title: `Need help planning the ${item.destinationName} group journey?`,
        body: "Use the quote form first, or contact support for children, accessibility needs, several pickup points, airport luggage or return collection planning.",
        nudgeText: `Need ${item.destinationName} minibus help?`,
      },
    },
  };
};

export const groupServiceConfigs = {
  heathrow: {
    configs: {
      minibus: {
        brand: "MinibusGrab",
        domain: "https://www.minibusgrab.com",
        className: "minibusgrab-v2-home",
        vehicle: "minibus",
        path: "/heathrow-minibus",
        title: "Heathrow Minibus Hire | 8 to 16 Seater Airport Transfers",
        description:
          "Book Heathrow minibus hire for families, hotels, teams and groups. 8 to 16 seater options, live Heathrow status, flight-aware planning and fixed-price quote flow.",
        keywords:
          "Heathrow minibus, Heathrow minibus hire, 8 seater Heathrow minibus, 16 seater Heathrow minibus, Heathrow group airport transfer, MinibusGrab Heathrow",
        h1: "Heathrow minibus hire with live airport intelligence",
        intro:
          "Fixed-price 8 to 16 seater group transfers for Heathrow terminals, hotels, families, teams and tours, planned with live airport, road, rail and queue signals.",
        quoteLabel: "Get Minibus Quote",
        formHeading: "Minibus transfers to or from Heathrow",
        pickupDefault: "",
        dropoffDefault: "",
        pickupAriaLabel: "Pickup address or postcode",
        dropoffAriaLabel: "Drop-off address or postcode",
        pickupPlaceholder: "Enter pickup address or postcode",
        dropoffPlaceholder: "Enter drop-off address or postcode",
        passengers: 8,
        bags: 0,
        resultPath: "/minibus-results",
        resultQuery: "vehicles=nine,fourteen",
        seats: "8 to 16 seaters",
        fleet: ["8 passenger / 9-seater minibuses", "14 to 16 passenger minibuses", "Licensed PSV partner options"],
      },
      coach: {
        brand: "CheapCoachGrab",
        domain: "https://www.cheapcoachgrab.com",
        className: "coachgrab-v2-home",
        vehicle: "coach",
        path: "/heathrow-coach",
        title: "Heathrow Coach Hire | 25 to 53 Seater Airport Transfers",
        description:
          "Book Heathrow coach hire for schools, companies, hotels, tours and large groups. 25 to 53 seater options, live Heathrow status and route intelligence.",
        keywords:
          "Heathrow coach, Heathrow coach hire, 25 seater Heathrow coach, 49 seater coach Heathrow, airport coach transfer Heathrow, CheapCoachGrab Heathrow",
        h1: "Heathrow coach hire planned with live intelligence",
        intro:
          "Coach transfers for Heathrow terminals, hotels, schools, companies, tours and large groups, planned around terminal status, road pressure and group timing.",
        quoteLabel: "Get Coach Quote",
        dropoffDefault: "",
        passengers: 25,
        bags: 0,
        resultPath: "/coach-results",
        resultQuery: "vehicle=twentyfive",
        seats: "25 to 53 seaters",
        fleet: ["25 seater mini coach", "33 to 49 seater coaches", "53 seater high-capacity options"],
      },
    },
    route: {
      routeKey: "heathrow",
      liveStripVariant: "heathrow",
      pageClassName: "heathrow-service-v2",
      heroEyebrow: "Heathrow group airport transfers",
      metrics: ["Terminals 2, 3, 4, 5", "Hotels + venues", "__SEATS__", "Live LHR checks"],
      areas: ["Heathrow Airport", "London", "Slough", "Windsor", "Maidenhead", "Ascot"],
      seo: {
        routeName: "Heathrow group airport transfer",
        serviceType: "Heathrow group airport transfer",
        areaServed: ["Heathrow Airport", "London", "Slough", "Windsor", "Maidenhead", "Ascot"],
      },
      trustCards: [
        {
          eyebrow: "GOV.UK registered",
          title: "Licensed operators",
          body: "Licensed and insured partners for group airport journeys.",
        },
        {
          eyebrow: "Live Heathrow status",
          title: "Road, rail, flight and queues",
          body: "Route context is shown before the booking decision.",
        },
      ],
      quote: {
        formId: "heathrow-service-quote",
        pickupDefault: "Heathrow Airport",
        dropoffDefault: "Central London",
        pickupAriaLabel: "Heathrow pickup terminal, postcode or address",
        dropoffAriaLabel: "Destination postcode or address",
        pickupPlaceholder: "Enter Heathrow terminal, postcode or address",
        dropoffPlaceholder: "Enter destination postcode or address",
        genericError:
          "We could not calculate a quote for this Heathrow group journey. Please check the details and try again.",
      },
      compactStatusTitle: "Live Heathrow group transfer checks",
      detailedStatusTitle: (vehicleLabel) => `Heathrow checks for ${vehicleLabel} group transfers`,
      detailedStatusIntro:
        "Roads, rail, flights, terminal pressure, queues and weather are grouped into a practical preview before the group commits to a quote.",
      reviews: heathrowReviews,
      proofCards: heathrowProofCards,
      chooseTitle: "Built for Heathrow groups",
      chooseCards: heathrowChooseCards,
      routeContentCards: heathrowRouteContentCards,
      faqItems: heathrowFaqItems,
      pricePreviewTitle: (vehicle) => `Popular Heathrow ${vehicle} previews`,
      routeOverviewTitle: "Heathrow group transfer notes",
      routeOverviewIntro:
        "Heathrow group transfers need more than a vehicle search. Terminal choice, flight timing, queue pressure, hotel loading, luggage and road conditions can all affect the plan.",
      vehicleContextTitle: (brand) => `${brand} Heathrow vehicle options`,
      faqEyebrow: "Heathrow group FAQ",
      faqTitle: "Common questions before booking",
      support: {
        title: "Need help planning a Heathrow group transfer?",
        body:
          "Use the quote form first, or contact support for multi-stop, hotel, school, corporate, tour, event or luggage-heavy Heathrow journeys.",
        nudgeText: "Need Heathrow group transfer help?",
      },
    },
  },
  gatwick: buildAirportMinibusService("gatwick"),
  luton: buildAirportMinibusService("luton"),
  stansted: buildAirportMinibusService("stansted"),
  manchester: buildAirportMinibusService("manchester"),
  legoland: legolandMinibusService,
  "heathrow-legoland": buildAttractionRouteService("heathrow-legoland"),
  "london-legoland": buildAttractionRouteService("london-legoland"),
  "heathrow-windsor-castle": buildAttractionRouteService("heathrow-windsor-castle"),
  "thorpe-park": buildAttractionRouteService("thorpe-park"),
};

export default groupServiceConfigs;
